// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { Link, useNavigate } from 'react-router-dom';
import { AppIcon } from '@loa/ui-components';

interface BreadcrumbItem {
  label: string;
  to?: string;
}

type BreadcrumbProps = Readonly<{
  items: BreadcrumbItem[];
  backTo?: string;
  hideBackButton?: boolean;
}>;

export function Breadcrumb({ items, backTo, hideBackButton = false }: BreadcrumbProps) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-3">
      {!hideBackButton && (
      <button
        type="button"
        onClick={() => navigate(backTo || '/relationships')}
        className="w-[48px] h-[48px] cursor-pointer flex items-center justify-center rounded-full bg-lilac-05 hover:bg-lilac-04 transition-colors text-black"
        aria-label="Go back"
      >
        <AppIcon name="chevron left rounded" size={24} />
      </button>
      )}
      <span className="font-sans text-[1.25em] leading-normal text-black">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <span key={index}>
              {index > 0 && (
                <span className="font-sans text-body-02 font-normal leading-normal !text-grey-04 px-sm">/</span>
              )}
              {isLast && !item.to ? (
                <span className="font-sans text-body-02 font-medium leading-normal text-black capitalize">{item.label}</span>
              ) : isLast ? (
                // Current-page crumb that is still navigable (e.g. "Proposals"
                // pointing at the relationship's Proposals tab).
                <Link to={item.to!} className="font-sans text-body-02 font-medium leading-normal !text-black !no-underline hover:!underline capitalize">{item.label}</Link>
              ) : (
                <Link to={item.to!} className="text-body-02 font-normal leading-normal !text-grey-04 font-sans !no-underline hover:!underline capitalize">{item.label}</Link>
              )}
            </span>
          );
        })}
      </span>
    </div>
  );
}
